/**
 * Synth sources: additive (PeriodicWave from partials), wavetable (saw/square/triangle/sine, supersaw = 7 detuned
 * saws, organ = drawbar partials), FM (modulator → gain → carrier.frequency), Shepard (octave-spaced sines under a
 * Gaussian, cycling), and pink noise (pitched only by the voice's comb/bp filter).
 */
import { glideTo, targetTau, type F32 } from '../params';
import { ticker } from '../modulation';
import { effectiveHz, safeStop, disconnectAll, type SourceUnit, type UnitConfig } from '../units';
import { shepardState } from './shepard';

export const SUPERSAW_CENTS = [-22, -14, -7, 0, 7, 14, 22];
/** Hammond-style drawbars as harmonics of hz/2: 16', 8', 5⅓', 4', 2⅔', 2', 1⅗', 1⅓', 1' */
export const ORGAN_HARMONICS: Array<[number, number]> = [[1, 0.5], [2, 1], [3, 0.6], [4, 0.7], [6, 0.35], [8, 0.3], [10, 0.2], [12, 0.15], [16, 0.1]];

export function partialsWave(ctx: BaseAudioContext, partials: readonly number[]): PeriodicWave {
  const n = Math.max(2, partials.length + 1);
  const real = new Float32Array(n);
  const imag = new Float32Array(n);
  partials.forEach((a, i) => { imag[i + 1] = a; });
  return ctx.createPeriodicWave(real, imag);
}

export function organWave(ctx: BaseAudioContext): PeriodicWave {
  const real = new Float32Array(17);
  const imag = new Float32Array(17);
  for (const [h, a] of ORGAN_HARMONICS) imag[h] = a;
  return ctx.createPeriodicWave(real, imag);
}

export const fallbackPartials = (n = 10): number[] => Array.from({ length: n }, (_, i) => 1 / (i + 1));

/** Paul Kellet's economy pink noise filter over white noise. */
export function makePinkNoise(frames: number, seed = 7): F32 {
  let s = seed >>> 0 || 1;
  const rand = () => { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; return ((s >>> 0) / 4294967296) * 2 - 1; };
  const out = new Float32Array(frames);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < frames; i++) {
    const w = rand();
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    out[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
    b6 = w * 0.115926;
  }
  return out;
}

const pinkCache = new WeakMap<BaseAudioContext, AudioBuffer>();
export function pinkNoiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  let b = pinkCache.get(ctx);
  if (!b) {
    const frames = Math.floor(ctx.sampleRate * 4);
    b = ctx.createBuffer(2, frames, ctx.sampleRate);
    b.copyToChannel(makePinkNoise(frames, 11), 0);
    b.copyToChannel(makePinkNoise(frames, 23), 1);
    pinkCache.set(ctx, b);
  }
  return b;
}

abstract class OscUnit implements SourceUnit {
  readonly output: GainNode;
  protected readonly oscs: OscillatorNode[] = [];
  protected readonly extra: AudioNode[] = [];
  protected readonly modulators: AudioNode[] = [];
  constructor(protected readonly cfg: UnitConfig) {
    this.output = cfg.ctx.createGain();
    this.output.gain.value = cfg.level;
  }
  protected osc(type: OscillatorType | PeriodicWave, detuneCents = 0): OscillatorNode {
    const o = this.cfg.ctx.createOscillator();
    if (typeof type === 'string') o.type = type as OscillatorType; else o.setPeriodicWave(type as PeriodicWave);
    o.detune.value = detuneCents;
    this.oscs.push(o);
    return o;
  }
  abstract setPitch(hz: number, glideSec: number, t: number): void;
  start(t: number, hz: number): void {
    this.setFrequencies(effectiveHz(hz, this.cfg.cents), t, 0);
    for (const o of this.oscs) o.start(t);
  }
  protected abstract setFrequencies(hz: number, t: number, glideSec: number): void;
  attachDetuneModulator(node: AudioNode): void {
    this.modulators.push(node);
    for (const o of this.oscs) node.connect(o.detune);
  }
  stop(t: number): void { for (const o of this.oscs) safeStop(o, t); }
  dispose(): void {
    const now = this.cfg.ctx.currentTime;
    for (const o of this.oscs) safeStop(o, now);
    disconnectAll(this.oscs); disconnectAll(this.extra);
    try { this.output.disconnect(); } catch { /* gone */ }
  }
}

export class AdditiveUnit extends OscUnit {
  constructor(cfg: UnitConfig, partials?: readonly number[]) {
    super(cfg);
    const p = partials && partials.length > 0 ? partials : fallbackPartials();
    this.osc(partialsWave(cfg.ctx, p)).connect(this.output);
  }
  protected setFrequencies(hz: number, t: number, glide: number): void {
    for (const o of this.oscs) glideTo(o.frequency, hz, t, glide);
  }
  setPitch(hz: number, glideSec: number, t: number): void { this.setFrequencies(effectiveHz(hz, this.cfg.cents), t, glideSec); }
}

export type WavetableName = 'saw' | 'square' | 'triangle' | 'sine' | 'supersaw' | 'organ';
const OSC_TYPES: Record<string, OscillatorType> = { saw: 'sawtooth', square: 'square', triangle: 'triangle', sine: 'sine' };

export class WavetableUnit extends OscUnit {
  private readonly ratio: number;
  constructor(cfg: UnitConfig, table: WavetableName) {
    super(cfg);
    this.ratio = table === 'organ' ? 0.5 : 1;
    if (table === 'supersaw') {
      const g = 1 / Math.sqrt(SUPERSAW_CENTS.length);
      for (const c of SUPERSAW_CENTS) {
        const gain = cfg.ctx.createGain();
        gain.gain.value = g;
        this.osc('sawtooth', c).connect(gain);
        gain.connect(this.output);
        this.extra.push(gain);
      }
    } else if (table === 'organ') {
      this.osc(organWave(cfg.ctx)).connect(this.output);
    } else {
      this.osc(OSC_TYPES[table] ?? 'sawtooth').connect(this.output);
    }
  }
  protected setFrequencies(hz: number, t: number, glide: number): void {
    for (const o of this.oscs) glideTo(o.frequency, hz * this.ratio, t, glide);
  }
  setPitch(hz: number, glideSec: number, t: number): void { this.setFrequencies(effectiveHz(hz, this.cfg.cents), t, glideSec); }
}

export class FmUnit extends OscUnit {
  private readonly modGain: GainNode;
  constructor(cfg: UnitConfig, private readonly ratio: number, private readonly index: number) {
    super(cfg);
    const carrier = this.osc('sine');
    const mod = this.osc('sine');
    this.modGain = cfg.ctx.createGain();
    mod.connect(this.modGain);
    this.modGain.connect(carrier.frequency);
    carrier.connect(this.output);
    this.extra.push(this.modGain);
  }
  protected setFrequencies(hz: number, t: number, glide: number): void {
    const [carrier, mod] = this.oscs;
    if (!carrier || !mod) return;
    const modHz = hz * this.ratio;
    glideTo(carrier.frequency, hz, t, glide);
    glideTo(mod.frequency, modHz, t, glide);
    glideTo(this.modGain.gain, this.index * modHz, t, glide);
  }
  setPitch(hz: number, glideSec: number, t: number): void { this.setFrequencies(effectiveHz(hz, this.cfg.cents), t, glideSec); }
  attachDetuneModulator(node: AudioNode): void {
    // only the carrier (keeping the ratio would need audio-rate multiplication)
    const carrier = this.oscs[0];
    if (carrier) node.connect(carrier.detune);
  }
}

export class ShepardUnit extends OscUnit {
  private readonly gains: GainNode[] = [];
  private phase = 0;
  private centerHz = 220;
  private untick: (() => void) | null = null;
  constructor(cfg: UnitConfig, private readonly rate: number, private readonly octaves: number) {
    super(cfg);
    for (let i = 0; i < octaves; i++) {
      const g = cfg.ctx.createGain();
      g.gain.value = 0;
      this.osc('sine').connect(g);
      g.connect(this.output);
      this.gains.push(g);
      this.extra.push(g);
    }
    this.output.gain.value = cfg.level / Math.sqrt(octaves);
  }
  private apply(t: number, tau: number): void {
    const st = shepardState(this.centerHz, this.phase, this.octaves);
    for (let i = 0; i < this.octaves; i++) {
      targetTau(this.oscs[i]!.frequency, st.hz[i]!, t, tau);
      targetTau(this.gains[i]!.gain, st.gain[i]!, t, tau);
    }
  }
  protected setFrequencies(hz: number, t: number, glide: number): void {
    this.centerHz = hz;
    this.apply(t, glide > 0 ? glide / 4.6 : 0.01);
  }
  start(t: number, hz: number): void {
    super.start(t, hz);
    if (this.rate !== 0) {
      this.untick = ticker.add((now, dt) => {
        this.phase += (this.rate * dt) / this.octaves;
        this.phase -= Math.floor(this.phase);
        this.apply(now, 0.05);
      });
    }
  }
  setPitch(hz: number, glideSec: number, t: number): void { this.setFrequencies(effectiveHz(hz, this.cfg.cents), t, glideSec); }
  dispose(): void { this.untick?.(); super.dispose(); }
}

export class NoiseUnit implements SourceUnit {
  readonly output: GainNode;
  private readonly src: AudioBufferSourceNode;
  constructor(private readonly cfg: UnitConfig) {
    this.output = cfg.ctx.createGain();
    this.output.gain.value = cfg.level;
    this.src = cfg.ctx.createBufferSource();
    this.src.buffer = pinkNoiseBuffer(cfg.ctx);
    this.src.loop = true;
    this.src.connect(this.output);
  }
  start(t: number): void {
    const b = this.src.buffer;
    this.src.start(t, b ? this.cfg.phaseScatter * Math.random() * b.duration : 0);
  }
  setPitch(): void { /* pitched by the voice filter (comb / bp key tracking) */ }
  attachDetuneModulator(): void { /* nothing to detune */ }
  stop(t: number): void { safeStop(this.src, t); }
  dispose(): void { safeStop(this.src, this.cfg.ctx.currentTime); try { this.src.disconnect(); this.output.disconnect(); } catch { /* gone */ } }
}
